"use client";

import { useState } from "react";
import Link from "next/link";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AnimateOnScroll } from "@/components/shared/animate-on-scroll";

const ROLES = [
  "Lekarz weterynarii",
  "Technik weterynaryjny",
  "Manager / Właściciel",
  "Inne",
];

export function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [city, setCity] = useState("");
  const [website, setWebsite] = useState(""); // Honeypot field

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          phone,
          role,
          clinic_name: clinicName,
          city,
          website, // Honeypot field
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Wystąpił błąd. Spróbuj ponownie.");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wystąpił błąd");
    } finally {
      setIsLoading(false);
    }
  }

  if (submitted) {
    return (
      <AnimateOnScroll>
        <div className="text-center py-16">
          <CheckCircle2 className="size-12 text-emerald-600 mx-auto" />
          <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">
            Dziękujemy!
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-md mx-auto">
            Odezwiemy się mailowo lub telefonicznie w ciągu 24h, aby ustalić
            termin demo.
          </p>
        </div>
      </AnimateOnScroll>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            placeholder="twoj@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Telefon</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="+48 123 456 789"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Kim jesteś?</Label>
        <div className="flex flex-wrap gap-2">
          {ROLES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              disabled={isLoading}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                role === r
                  ? "border-primary bg-primary/10 text-primary font-medium"
                  : "border-border text-muted-foreground hover:border-foreground/30"
              } disabled:opacity-50`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="clinic">Nazwa placówki</Label>
          <Input
            id="clinic"
            type="text"
            placeholder="Klinika Weterynaryjna..."
            value={clinicName}
            onChange={(e) => setClinicName(e.target.value)}
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="city">Miasto</Label>
          <Input
            id="city"
            type="text"
            placeholder="Warszawa"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Honeypot field - hidden from users, filled by bots */}
      <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true">
        <Label htmlFor="contact-website">Website</Label>
        <Input
          id="contact-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full sm:w-auto text-base px-8"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Wysyłanie...
          </>
        ) : (
          "Wyślij zgłoszenie"
        )}
      </Button>

      <p className="text-xs text-muted-foreground leading-relaxed max-w-xl">
        Klikając „Wyślij zgłoszenie", wyrażam zgodę na przetwarzanie moich danych
        osobowych przez Vetina Prosta Spółka Akcyjna, ul. Konwaliowa 79 63-004
        Tulce, KRS: 0001226831, NIP: 7773464768, w celu przesyłania informacji handlowych drogą
        elektroniczną na zasadach określonych w{" "}
        <Link
          href="/regulamin"
          className="text-primary hover:underline"
          target="_blank"
        >
          Regulaminie
        </Link>{" "}
        oraz{" "}
        <Link
          href="/polityka-prywatnosci"
          className="text-primary hover:underline"
          target="_blank"
        >
          Polityce prywatności
        </Link>
        .
      </p>
    </form>
  );
}
